import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { LightboxService } from 'src/app/shared/components/common/lightbox/lightbox.service';
import { v4 } from 'uuid';
import { getMarkerJsArea } from '../../../../services/marker-js-generator';
import { IAttachmentWithData } from '../../document-attachment.service';
import { DocumentEditService } from '../../document-edit.service';
import { SwodocNodeImageService } from './swodoc-node-image.service';

@Component({
  selector: 'swodoc-node-image',
  templateUrl: 'swodoc-node-image.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SwodocNodeImageService],
})
export class SwodocNodeImage implements OnInit {
  @Input() node: AuditNodeViewModel;
  @Input() documentEditService: DocumentEditService;
  @Output() changed = new EventEmitter<AuditNodeViewModel>();

  @ViewChild('file', { static: true })
  public fileInputElementRef: ElementRef;

  public images: Partial<IAttachmentWithData>[];
  @ViewChild('markerImageElement', { static: true })
  public markerImageElementRef: ElementRef;
  public markerImage: string;

  constructor(
    private _changeDetectionRef: ChangeDetectorRef,
    private _lightbox: LightboxService,
    private _platform: Platform,
    private _swodocNodeImageService: SwodocNodeImageService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.images = this.documentEditService.getImagesForNodeId(this.node.id);
  }

  public async addImage(): Promise<void> {
    if (this._platform.is('hybrid')) {
      return this._swodocNodeImageService.showSelectImageActionSheet(this.addImageToImagesArray.bind(this));
    }
    this.fileInputElementRef.nativeElement.click();
  }

  public async handleFileOpenDialog(event: Event): Promise<void> {
    const image = await this._swodocNodeImageService.fileAsBase64((event.target as any).files[0]);
    this.addImageToImagesArray(image, true);
    // Clear the filename input in order to allow upload of the same file multiple times.
    // Otherwise the change event would not fire
    (event.target as any).value = null;
  }

  private async openMarkerField(image: string): Promise<string> {
    return new Promise(resolve => {
      this.markerImage = image;
      this._changeDetectionRef.detectChanges();
      setTimeout(() => {
        const markerArea = getMarkerJsArea(this.markerImageElementRef);
        markerArea.addRenderEventListener(dataUrl => resolve(dataUrl));
        markerArea.addCloseEventListener(() => resolve(null));
        markerArea.show();
      });
    });
  }

  public async showEditImageActionSheet(index) {
    const action = await this._swodocNodeImageService.showEditImageActionSheet();
    if (!action) {
      return;
    }
    switch (action) {
      case 'show':
        await this._lightbox.show(
          this.images.map(image => image.data),
          index,
          true
        );
        break;
      case 'edit':
        const result = await this.openMarkerField(this.images[index].data);
        if (result) {
          this.documentEditService.removeImage(this.images[index]._id);
          this.images = this.images.filter(item => item._id !== this.images[index]._id);
          this.addImageToImagesArray(result);
        }
        break;
      case 'delete':
        this.documentEditService.removeImage(this.images[index]._id);
        this.images = this.images.filter(item => item._id !== this.images[index]._id);
        this._cdr.markForCheck();
        break;
    }
  }

  private async addImageToImagesArray(image, showMarker?: boolean) {
    let imageToSave = image;
    if (showMarker) {
      imageToSave = await this.openMarkerField(image);
    }
    if (imageToSave) {
      const newImage = {
        _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
        metadata: {
          auditNodeId: this.node.id,
          type: 100, // SwodocImage
        },
        data: imageToSave,
      } as Partial<IAttachmentWithData>;
      this.documentEditService.addImage(newImage);
      this.images.push(newImage);
      this._cdr.markForCheck();
    }
  }
}
